import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, Star, Cpu, Zap, MemoryStick, Calendar, Layers, ThumbsUp, ThumbsDown, Tag, CheckCircle, XCircle, TrendingDown, RefreshCw, Loader2, Home } from 'lucide-react';
import { gpuData, getTierForPerformance } from '@/data/gpuData';
import { getRayTracingBenchmarks } from '@/data/rayTracingData';
import { getGPUReview } from '@/data/gpuReviews';
import { getGPUPricing, getLowestPrice } from '@/data/vendorPrices';
import { useGPUPrice } from '@/hooks/useGPUPrices';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { GPUReviews } from '@/components/GPUReviews';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';

const brandColors = {
  nvidia: 'from-green-500 to-green-600',
  amd: 'from-red-500 to-red-600',
  intel: 'from-blue-500 to-blue-600',
};

const GPUDetailPage = () => {
  const { gpuId } = useParams<{ gpuId: string }>();
  const gpu = gpuData.find(g => g.id === gpuId);

  if (!gpu) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">GPU Not Found</h1>
          <Link to="/gpu-comparison">
            <Button>
              <Home className="w-4 h-4 mr-2" />
              Back to Comparison
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const tier = getTierForPerformance(gpu.performance);
  const rtBenchmarks = getRayTracingBenchmarks(gpu.id);
  const review = getGPUReview(gpu.id);
  
  // Use real-time prices from database, fallback to static data
  const { priceData: livePriceData, loading: pricesLoading, refreshPrices } = useGPUPrice(gpu.id);
  
  // Fallback to static data if no live prices
  const staticPricing = getGPUPricing(gpu.id);
  const staticLowestPrice = getLowestPrice(gpu.id);
  
  // Use live data if available, otherwise use static
  const hasLivePrices = livePriceData && livePriceData.vendors.length > 0;
  const displayPricing = hasLivePrices ? livePriceData : staticPricing;
  const displayLowestPrice = hasLivePrices ? livePriceData?.lowestPrice : staticLowestPrice;

  return (
    <>
      <Helmet>
        <title>{gpu.name} Specs, Review & Benchmarks | GPU Comparison</title>
        <meta name="description" content={`Full specifications, reviews, ray tracing benchmarks, and best prices for ${gpu.name}. ${gpu.vram} VRAM, ${gpu.tdp}W TDP, ${gpu.architecture} architecture.`} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/gpu-comparison" className="flex items-center gap-1">
                    <Home className="w-4 h-4" />
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/gpu-comparison#comparison">GPU Comparison</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{gpu.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Hero Section */}
          <div className={`rounded-2xl bg-gradient-to-r ${brandColors[gpu.brand]} p-8 mb-8 text-white`}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <Badge variant="secondary" className="mb-2 bg-white/20 text-white border-0">
                  {gpu.brand.toUpperCase()}
                </Badge>
                <h1 className="text-4xl font-bold mb-2">{gpu.name}</h1>
                <p className="text-white/80">{gpu.architecture} • Released {gpu.releaseYear}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">${gpu.msrp}</div>
                <div className="text-white/80">MSRP</div>
                {tier && (
                  <Badge variant="secondary" className="mt-2 bg-white/20 text-white border-0">
                    {tier.name}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* Specifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="w-5 h-5" />
                    Full Specifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <SpecItem icon={<Layers className="w-5 h-5" />} label="Architecture" value={gpu.architecture} />
                    <SpecItem icon={<MemoryStick className="w-5 h-5" />} label="VRAM" value={gpu.vram} />
                    <SpecItem icon={<Zap className="w-5 h-5" />} label="TDP" value={`${gpu.tdp}W`} />
                    <SpecItem icon={<Calendar className="w-5 h-5" />} label="Release Year" value={gpu.releaseYear.toString()} />
                  </div>

                  <Separator className="my-6" />

                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Relative Performance</span>
                      <span className="font-semibold">{gpu.performance}%</span>
                    </div>
                    <Progress value={gpu.performance} className="h-3" />
                    <p className="text-xs text-muted-foreground mt-1">Compared to RTX 4090 baseline (100%)</p>
                  </div>
                </CardContent>
              </Card>

              {/* Ray Tracing Benchmarks */}
              {rtBenchmarks.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-500" />
                      Ray Tracing Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {rtBenchmarks.map((benchmark, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                          <div>
                            <div className="font-semibold">{benchmark.game}</div>
                            <div className="text-sm text-muted-foreground">
                              {benchmark.resolution} • {benchmark.settings}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${benchmark.fps >= 60 ? 'text-green-500' : benchmark.fps >= 30 ? 'text-yellow-500' : 'text-red-500'}`}>
                              {benchmark.fps} FPS
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Review Summary */}
              {review && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Review Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="text-4xl font-bold">{review.rating}</div>
                      <div>
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-5 h-5 ${i < Math.floor(review.rating / 2) ? 'fill-yellow-500 text-yellow-500' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                        <div className="text-sm text-muted-foreground">{review.source}</div>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="flex items-center gap-2 text-green-500 font-semibold mb-3">
                          <ThumbsUp className="w-4 h-4" />
                          Pros
                        </div>
                        <ul className="space-y-2">
                          {review.pros.map((pro, index) => (
                            <li key={index} className="text-sm flex items-start gap-2">
                              <span className="text-green-500 mt-1">•</span>
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-red-500 font-semibold mb-3">
                          <ThumbsDown className="w-4 h-4" />
                          Cons
                        </div>
                        <ul className="space-y-2">
                          {review.cons.map((con, index) => (
                            <li key={index} className="text-sm flex items-start gap-2">
                              <span className="text-red-500 mt-1">•</span>
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="font-semibold mb-2">Verdict</div>
                      <p className="text-muted-foreground">{review.verdict}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* User Reviews */}
              <GPUReviews gpuId={gpu.id} gpuName={gpu.name} />
            </div>

            {/* Sidebar - Purchase Links */}
            <div className="space-y-6">
              {/* Lowest Price Card */}
              {displayLowestPrice && (
                <Card className="border-green-500/50 bg-green-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-green-600">
                      <TrendingDown className="w-5 h-5" />
                      Lowest Price
                      {pricesLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      ${displayLowestPrice.price}
                    </div>
                    <div className="text-sm text-muted-foreground mb-3">
                      at {displayLowestPrice.retailer}
                    </div>
                    <a href={displayLowestPrice.url} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        Buy at {displayLowestPrice.retailer}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              )}

              {/* All Vendor Prices */}
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Tag className="w-5 h-5" />
                      Compare Prices
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={refreshPrices}
                      disabled={pricesLoading}
                      aria-label="Refresh prices"
                    >
                      <RefreshCw className={cn("w-4 h-4", pricesLoading && "animate-spin")} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {pricesLoading && !displayPricing ? (
                    <div className="space-y-2">
                      {[1,2,3,4,5].map(i => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : displayPricing?.vendors.map((vendor, index) => (
                    <a
                      key={index}
                      href={vendor.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <div className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-all hover:bg-muted/50',
                        vendor.inStock && vendor.price === displayLowestPrice?.price && 'border-green-500/50 bg-green-500/5',
                        !vendor.inStock && 'opacity-60'
                      )}>
                        <div className="flex items-center gap-2">
                          {vendor.inStock ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="font-medium">{vendor.retailer}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {vendor.price ? (
                            <span className={cn(
                              'font-bold',
                              vendor.price === displayLowestPrice?.price && 'text-green-600'
                            )}>
                              ${vendor.price}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">N/A</span>
                          )}
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </a>
                  ))}
                  
                  {!displayPricing && !pricesLoading && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Price data not available
                    </p>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-4 pt-4 border-t">
                    * Prices update in real-time. Click refresh for latest. We may earn a commission from purchases.
                  </p>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">MSRP</span>
                    <span className="font-semibold">${gpu.msrp}</span>
                  </div>
                  {displayLowestPrice && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current Low</span>
                      <span className="font-semibold text-green-600">${displayLowestPrice.price}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Value Score</span>
                    <span className="font-semibold">{(gpu.performance / (gpu.msrp / 100)).toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Efficiency</span>
                    <span className="font-semibold">{(gpu.performance / (gpu.tdp / 100)).toFixed(1)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

const SpecItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="flex items-center gap-3">
    <div className="p-2 bg-muted rounded-lg text-muted-foreground">{icon}</div>
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  </div>
);

export default GPUDetailPage;
